/**
 * Whether the app currently has a network.
 *
 * `navigator.onLine` alone is famously optimistic — it reports true for a phone
 * associated with a wifi access point that has no route to anywhere, which is
 * precisely the situation in an outbuilding with a weak repeater. It is still
 * the right primary signal because it is instant; what it is not is proof.
 *
 * The honest thing to show a user is therefore not "offline" but "not synced",
 * which is a question about Firestore's write queue rather than about the radio.
 * `usePendingWrites` below answers that one; this hook only drives the network
 * chip. Do not use it to decide whether a write will succeed — Firestore's
 * queue makes that question meaningless, and any code branching on it is code
 * that will one day refuse to save a weight that would have saved fine.
 */
import { useSyncExternalStore } from 'react';

const subscribe = (onChange: () => void) => {
  window.addEventListener('online', onChange);
  window.addEventListener('offline', onChange);
  return () => {
    window.removeEventListener('online', onChange);
    window.removeEventListener('offline', onChange);
  };
};

export const useOnlineStatus = (): boolean =>
  useSyncExternalStore(
    subscribe,
    () => navigator.onLine,
    // Server snapshot: there is no server, but Vite's SSR-ish dev transforms and
    // any future prerender both need this to be deterministic.
    () => true,
  );
